import { LOCALES } from 'state/intl/types';
import { getLocale } from 'utils/intl/language';

/**
 * Creates a cache key for formatter instances
 * @param locale - The locale string
 * @param options - The formatting options
 * @returns A cache key string
 */
const createCacheKey = (locale: string, options?: Record<string, any>): string => {
  const optionsKey = options ? JSON.stringify(options) : '{}';
  return `${locale}|${optionsKey}`;
};

/**
 * Factory to create a memoized Intl formatter
 * @param Constructor - The Intl constructor (DateTimeFormat or NumberFormat)
 * @returns A function that returns a cached instance
 */
const createMemoizedFormatter = <O, T>(Constructor: new (l: string, o?: O) => T) => {
  const cache = new Map<string, T>();

  const getFormatter = (locale: string, options?: O): T => {
    const key = createCacheKey(locale, options);
    let instance = cache.get(key);

    if (!instance) {
      instance = new Constructor(locale, options);
      cache.set(key, instance);
    }

    return instance;
  };

  const clearCache = () => {
    cache.clear();
  };

  return { getFormatter, clearCache };
};

// Create memoized instances
const dateTimeFormatter = createMemoizedFormatter<Intl.DateTimeFormatOptions, Intl.DateTimeFormat>(
  Intl.DateTimeFormat
);
const numberFormatter = createMemoizedFormatter<Intl.NumberFormatOptions, Intl.NumberFormat>(
  Intl.NumberFormat
);

/**
 * Clear all formatter caches
 * Should be called when the locale changes to prevent stale instances
 */
export const clearFormattersCache = (): void => {
  dateTimeFormatter.clearCache();
  numberFormatter.clearCache();
};

/**
 * Formats a date according to the current locale
 * @param value - The date to format
 * @param options - The formatting options
 * @returns The formatted date string
 */
export const formatDate = (
  value: Date | number | string,
  options?: Intl.DateTimeFormatOptions
): string => {
  const date = typeof value === 'string' || typeof value === 'number' ? new Date(value) : value;
  const locale = getLocale();

  try {
    return dateTimeFormatter.getFormatter(locale, options).format(date);
  } catch (error) {
    if (__DEV__) {
      // eslint-disable-next-line no-console
      console.warn(`Error formatting date for locale "${locale}":`, error);
    }
    return date.toString();
  }
};

/**
 * Formats a time according to the current locale
 * @param value - The date to format
 * @param options - The formatting options (defaults to hour and minute)
 * @returns The formatted time string
 */
export const formatTime = (
  value: Date | number | string,
  options: Intl.DateTimeFormatOptions = { hour: 'numeric', minute: 'numeric' }
): string => {
  const date = typeof value === 'string' || typeof value === 'number' ? new Date(value) : value;
  const locale = getLocale() || LOCALES.en_us;

  try {
    return dateTimeFormatter.getFormatter(locale, options).format(date);
  } catch (error) {
    if (__DEV__) {
      // eslint-disable-next-line no-console
      console.warn(`Error formatting time for locale "${locale}":`, error);
    }
    return date.toString();
  }
};

/**
 * Formats a date to parts according to the current locale
 * @param value - The date to format
 * @param options - The formatting options
 * @returns An array of date parts with type and value
 */
export const formatDateToParts = (
  value: Date | number | string,
  options?: Intl.DateTimeFormatOptions
): Intl.DateTimeFormatPart[] => {
  const date = typeof value === 'string' || typeof value === 'number' ? new Date(value) : value;
  const locale = getLocale() || LOCALES.en_us;

  try {
    return dateTimeFormatter.getFormatter(locale, options).formatToParts(date);
  } catch (error) {
    if (__DEV__) {
      // eslint-disable-next-line no-console
      console.warn(`Error formatting date to parts for locale "${locale}":`, error);
    }
    // Return a fallback parts array
    return [{ type: 'literal', value: date.toString() }];
  }
};

/**
 * Formats a number according to the current locale
 * @param value - The number to format
 * @param options - The formatting options
 * @returns The formatted number string
 */
export const formatNumber = (value: number, options?: Intl.NumberFormatOptions): string => {
  const locale = getLocale() || LOCALES.en_us;

  try {
    return numberFormatter.getFormatter(locale, options).format(value);
  } catch (error) {
    if (__DEV__) {
      // eslint-disable-next-line no-console
      console.warn(`Error formatting number for locale "${locale}":`, error);
    }
    return value.toString();
  }
};
